<template>
  <div class="b-vehicle">
    <div class="b-vehicle__tagged">
      <span v-if="item.highlightText" class="e-guaranteed">
        {{ item.highlightText }}
      </span>
    </div>

    <div class="b-vehicle__body">
      <div class="b-vehicle__header mb-40">
        <div class="b-vehicle__title h4 extrabold">
          {{ item.make }} {{ item.model }}
        </div>
        <div class="b-vehicle__groups mb-10 semibold">
          <ul>
            <li data-bs-toggle="tooltip" :title="dictionary.groupTooltipText">
              {{ item.modelDescription }}
            </li>
          </ul>
        </div>
        <div class="b-vehicle__details">
          <ul>
            <li>
              <div class="pair bold">
                <i class="icon-transmission"></i>
                <span>
                  {{ item.transmissionType }}
                </span>
              </div>
            </li>
            <li>
              <div class="pair bold">
                <i class="icon-fuel"></i>
                {{ item.fuel }}
              </div>
            </li>
            <li v-if="item.emissions">
              <div class="pair bold">
                <i class="icon-emmission"></i>
                {{ item.emissions }} gr/km
              </div>
            </li>
            <li v-if="item.consumption">
              <div class="pair bold">
                <i class="icon-consumption"></i>
                {{ item.consumption }} lt/100km
              </div>
            </li>
            <li v-if="item.energyConsumption">
              <div class="pair bold">
                <i class="icon-consumption"></i>
                {{ item.energyConsumption }} KWh/100Km
              </div>
            </li>
          </ul>
        </div>
      </div>

      <SlickCarousel
        ref="slick"
        :key="'key-' + slickKey"
        class="b-vehicle__slick mb-30"
        :options="slickOptions">
        <div v-for="(image, index) in images" :key="index" class="item">
          <LazyImage v-if="image" :image="image" />
        </div>
      </SlickCarousel>

      <div class="b-vehicle__footer mb-30">
        <div class="b-vehicle__totals mb-20">
          <div
            class="b-vehicle__price-w-vat"
            :class="{ 'is-active': vatIncluded }">
            <div class="bold mb-5">
              <span>{{ dictionary.vehiclePricePrefix }}&nbsp;</span>
              <span class="animate">
                <em v-if="vatIncluded">{{ item.pricePerMonthIncldVat }}</em>
                <em v-else>{{ item.pricePerMonth }}</em>
                {{ dictionary.vehiclePriceSuffix }}
              </span>
            </div>

            <div class="form-check form-switch">
              <label
                class="form-check-label"
                :style="{ cursor: isPointer }"
                :for="'vat' + item.id">
                <span v-if="vatIncluded">
                  {{ dictionary.vehicleVatIncluded }}
                </span>
                <span v-else>{{ dictionary.vehicleVatExcluded }}</span>
              </label>
              <input
                v-if="!isDisabled"
                :id="'vat' + item.id"
                v-model="vatIncluded"
                class="form-check-input"
                type="checkbox"
                role="switch" />
            </div>
          </div>
          <Anchor
            v-if="item.link"
            :item="item.link"
            class="btn btn-primary btn-full-width" />
        </div>
      </div>

      <hr class="separator mb-12" />

      <div class="b-vehicle__payments">
        <ul>
          <li v-if="item.prepaymentInteger">
            <span class="label">{{ dictionary.paymentInAdvanceLabel }}</span>
            <em v-if="!vatIncluded">{{ item.prepayment }}</em>
            <em v-else>{{ priceHasValue(item.paymentInAdvanceIncldVat) }}</em>
          </li>
          <li v-if="item.retailPrice">
            <span class="label">{{ dictionary.retailPriceLabel }}</span>
            <em>{{ item.retailPrice }}</em>
          </li>
          <li v-if="item.retailPriceBeforeTaxesShow">
            <span class="label">
              {{ dictionary.retailPriceBeforeTaxesLabel }}
            </span>
            <em>{{ item.retailPriceBeforeTaxes }}</em>
          </li>
        </ul>
      </div>

      <div
        v-if="showCompare"
        class="d-none d-lg-flex justify-content-center mt-20">
        <div class="form-check">
          <input
            :id="'checkboxCompare' + item.id"
            v-model="isChecked"
            autocomplete="off"
            class="form-check-input"
            type="checkbox"
            :disabled="disabledCompare" />
          <label class="form-check-label" :for="'checkboxCompare' + item.id">
            {{ dictionary.compareLabel }}
          </label>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { v4 as uuidv4 } from 'uuid'
import Anchor from './common/Anchor.vue'
import LazyImage from './common/LazyImage.vue'
import SlickCarousel from '../components/common/SlickCarousel.vue'
import mixinsLeasingOffers from './../mixins/leasingOffers-mixins'

export default {
  components: { Anchor, LazyImage, SlickCarousel },
  mixins: [mixinsLeasingOffers],
  props: {
    item: {
      required: true,
      type: Object,
    },
    showCompare: {
      required: false,
      type: Boolean,
    },
    isAlreadyChecked: {
      required: false,
      type: Boolean,
    },
  },
  data() {
    return {
      vatIncluded: false,
      slickKey: uuidv4(),
      slickOptions: {
        infinite: true,
        autoplay: false,
        arrows: true,
        dots: false,
        slidesToShow: 1,
        slidesToScroll: 1,
        centerMode: false,
        variableWidth: false,
      },
    }
  },
  computed: {
    isPointer() {
      if (
        this.item.pricePerMonthIncldVatInteger > 0 &&
        this.item.pricePerMonthIncldVatInteger
      ) {
        return 'pointer'
      }
      return 'default'
    },
    isChecked: {
      get: function () {
        if (!this.recordsCompare.length) {
          return false
        }

        return this.recordsCompare
          .map((x) => x.id)
          .some((x) => x === this.item.id)
      },
      set: function (value) {
        value
          ? this.addRecordToCompare({ value: this.item })
          : this.removeRecordFromCompare({ value: this.item.id })
      },
    },
    disabledCompare() {
      if (this.recordsCompare.map((i) => i.id).includes(this.item.id)) {
        return false
      }

      return this.recordsCompare.length >= this.configuration.compareMaxItems
    },
    guaranteedColor() {
      return this.item.highlightBgColor
    },
    isDisabled() {
      if (
        this.item.pricePerMonthIncldVatInteger === 0 ||
        !this.item.pricePerMonthIncldVatInteger
      ) {
        return true
      }
      return false
    },
    images() {
      const images = this.item.galleryImages
      if (!images.length) {
        return []
      }
      return images.map((x) => ({ url: x.url, alt: x.alt }))
    },
    saverPrice() {
      if (!this.item.saverPrices) {
        return ''
      }
      return this.item.saverPrices[0].price
    },
    saverPlusPrice() {
      if (!this.item.flexPrices) {
        return ''
      }
      return this.item.flexPrices[0].price
    },
  },
  mounted() {
    if (this.showCompare) {
      this.isChecked = this.isAlreadyChecked
    }
  },
  methods: {
    priceHasValue(price) {
      if (price === null || price === undefined) return '-'
      if (price.length <= 1) return '-'

      const priceWithoutEuroSign = price.slice(1)

      return priceWithoutEuroSign > 0 ? price : '-'
    },
  },
}
</script>
<style lang="scss" scoped>
.e-guaranteed {
  background-color: v-bind(guaranteedColor);
  color: white;
}
.b-vehicle__groups {
  height: 40px;
}
</style>
